/* global React, G */
import { forwardRef, useCallback, useContext, useLayoutEffect, useMemo, useState } from 'react'
import ApplicationContext from '@platform/react/context/application'

/**
 * Generic HOC for listening to `G.NOTIFICATION` events and
 * managing a state for its child.
 *
 * @example
 * `G.NOTIFICATION, G.INIT, type` - Trigger initial fetching of data
 * `G.NOTIFICATION, G.DESTROY, type` - Cleanup before unmounting
 * `G.NOTIFICATION, G.DONE, type` - Get new notification data and fill state
 *
 * @param {React.ReactNode} Component Component to use for rendering
 * @param {boolean} [hidden]          whether the component should be rendered
 * @param {Object} props              additional props
 * @returns {function(*)}
 */
const NotificationHOC = Component => ({ hidden, ...props }, ref) => {
  const { options: { notification }, child: { route } } = props
  const { eventBus, session } = useContext(ApplicationContext)
  const [state, setState] = useState({})

  const canEmit = useMemo(() => !hidden
    && notification
    // We can set notification.hidden to inhibit the init event from firing
    // in case we need to fire it from somewhere else (like app init)
    && !notification?.hidden
    && session[G.META], [hidden, session, notification])

  const _msgEventName = eventBus.type(G.NOTIFICATION, G.DONE, notification?.type)
  const _msgEventHandler = useCallback(({ detail }) => {
    const { [G.DATA]: data } = detail
    setState(prevState => ({ ...prevState, ...data }))
  }, [])

  // Get initial data when the component first renders
  useLayoutEffect(() => {
    if (canEmit) {
      eventBus.add(_msgEventName, _msgEventHandler)
      eventBus.dispatch(eventBus.type(G.NOTIFICATION, G.INIT), { [G.DATA]: {
        [G.GROUP]: notification.type,
        [G.API]: notification.api,
        [G.ROUTE]: notification.route,
        [G.MENU]: notification?.menu,
      } })
    }

    return () => {
      if (canEmit) {
        eventBus.remove(_msgEventName, _msgEventHandler)
        eventBus.dispatch(eventBus.type(G.NOTIFICATION, G.DESTROY, notification.type), { id: null })
      }
    }
  }, [canEmit])

  return hidden ? null : (
    <Component
      {...props}
      ref={ref}
      group={notification?.type}
      notification={state}
    />
  )
}

export default Component => forwardRef(NotificationHOC(Component))
